import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, ChefHat, Home as HomeIcon, Users, Star, Quote, MessageCircle, Phone, Mail,
  Gift, CalendarCheck, ChevronDown,
} from "lucide-react";
import {
  Ornament, Reveal, Kicker, Titel, PrimaryBtn, GhostBtn, FotoKarte, FotoBand,
} from "@/components/vorschau/Bausteine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEGE = [
  {
    nr: "01", icon: ChefHat, title: "Mietkoch für Betriebe",
    text: "Ich stehe selbst bei Ihnen am Herd — à la carte, Bankett, Frühstück oder Catering. Stundenweise, kurzfristig, ohne Einarbeitungsverlust.",
    meta: "ab 49 € netto / Std · Werktag", to: "/anfrage-betrieb", cta: "Einsatz anfragen",
    bild: "/event-kueche.jpg", testid: "vorschau-weg-mietkoch",
  },
  {
    nr: "02", icon: HomeIcon, title: "Privatkoch für Zuhause",
    text: "Ihr Menü-Abend in Ihrer eigenen Küche: Einkauf, Vorbereitung, Service und eine aufgeräumte Küche zum Abschied. Ab 2 Gästen — größere Runden auf Anfrage.",
    meta: "Festpreis pro Person", to: "/privatkoch", cta: "Private Dining entdecken",
    bild: "/event-bg-teller.jpg", testid: "vorschau-weg-privatkoch",
  },
  {
    nr: "03", icon: Users, title: "Koch-Vermittlung",
    text: "Wenn ich selbst keine Zeit habe: Ich vermittle geprüfte selbstständige Köche aus meinem Netzwerk — an Betriebe und an Privatkunden.",
    meta: "Betriebe · Privatkunden · Festanstellung", to: "/vermittlung", cta: "Vermittlung ansehen",
    bild: "https://images.unsplash.com/photo-1723083467111-e089b75c2adb?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80", testid: "vorschau-weg-vermittlung",
  },
];

const SAEULEN = [
  ["Verlässlich", "Zugesagt ist zugesagt — pünktlich, vorbereitet und mit eigenem Werkzeug."],
  ["Handwerk", "Fonds, Saucen, Patisserie: alles selbst gemacht, nichts aus der Tüte."],
  ["Mit Freude", "Ihre Gäste sollen staunen. Daran messe ich jeden Abend."],
];

const GALERIE = [
  { src: "/dessert-schokoladenmousse.jpg", titel: "Schokoladenmousse", text: "Mango · Himbeere · Pistazie", hoch: false },
  { src: "/event-bg-gericht.jpg", titel: "Hauptgang", text: "Regional eingekauft, auf den Punkt gegart", hoch: true },
  { src: "/event-bg-dessert.jpg", titel: "Süßer Abschluss", text: "Patisserie aus eigener Hand", hoch: false },
  { src: "/carousel-4-philosophie.jpg", titel: "Am Herd", text: "Offene Küche bei Ihnen zu Hause", hoch: true },
];

export default function StartVorschau() {
  const [reviews, setReviews] = useState([]);
  const [googleUrl, setGoogleUrl] = useState("");

  useEffect(() => {
    axios.get(`${API}/reviews/public`).then((r) => {
      setReviews(r.data?.reviews || []);
      setGoogleUrl(r.data?.google_url || "");
    }).catch(() => {});
  }, []);

  const schnitt = reviews.length
    ? (reviews.reduce((a, r) => a + Number(r.rating || 5), 0) / reviews.length).toFixed(1).replace(".", ",")
    : null;

  return (
    <div data-testid="page-vorschau-start" className="bg-[#FDF8F1] text-[#2B2320] selection:bg-[#BA6D52] selection:text-white">
      {/* 1 · Kino-Hero */}
      <section className="relative min-h-[88vh] sm:min-h-[92vh] overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-cover bg-center animate-kenburns"
          style={{ backgroundImage: "url('/event-bg-teller.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(18,16,12,0.72)" }} />
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#FDF8F1]" />

        <div className="relative w-full max-w-6xl mx-auto px-6 md:px-12 py-20 text-center">
          <img src="/logo.png" alt="Lukas Stutz"
            className="h-20 w-20 md:h-24 md:w-24 mx-auto rounded-full bg-[#FAF8F3]/90 p-2 animate-in fade-in zoom-in-90 duration-1000" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2C4B2] mt-7 animate-in fade-in slide-in-from-bottom-3 duration-1000 delay-150">
            Michelstadt · Odenwald · Rhein-Main
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#FFFBF4] mt-7 leading-[1.08] drop-shadow-[0_3px_18px_rgba(0,0,0,0.9)] animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-200">
            Ein Koch, der kommt,<br className="hidden sm:block" /> wenn es darauf ankommt.
          </h1>
          <p className="max-w-xl mx-auto text-base md:text-lg text-[#F2E7DA] mt-7 font-light leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)] animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
            Mietkoch für Gastronomie und Events, Privatkoch für Ihren Abend zu Hause.
            Frisch gekocht, verlässlich geplant — und ohne Stress für Sie.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-11 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500">
            <PrimaryBtn href="/anfrage-betrieb" data-testid="vorschau-hero-cta-anfrage">
              Einsatz anfragen <ArrowRight size={15} />
            </PrimaryBtn>
            <a href="/privatkoch" data-testid="vorschau-hero-cta-privat"
              className="inline-flex items-center justify-center gap-2.5 border border-white/45 text-[#FFFBF4] hover:bg-white/10 hover:border-white px-8 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300">
              Privatkoch für Zuhause
            </a>
          </div>

          {schnitt && (
            <a href="#vorschau-bewertungen" data-testid="vorschau-hero-rating"
              className="group inline-flex items-center gap-3 mt-12 text-[#EADFD2] hover:text-white transition-colors animate-in fade-in duration-1000 delay-700">
              <span className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={14} className="fill-[#D99A5B] text-[#D99A5B]" />
                ))}
              </span>
              <span className="text-xs tracking-wide">{schnitt} von 5 · {reviews.length} Google-Bewertungen</span>
              <ChevronDown size={14} className="transition-transform group-hover:translate-y-0.5" />
            </a>
          )}
        </div>
      </section>

      {/* 2 · Haltung */}
      <section className="max-w-3xl mx-auto px-6 py-20 sm:py-28 text-center">
        <Reveal testid="vorschau-haltung">
          <Ornament />
          <Quote size={22} className="text-[#BA6D52] mx-auto mt-9" />
          <p className="font-serif text-2xl sm:text-3xl md:text-4xl font-light leading-[1.35] mt-6">
            „Gutes Essen braucht keine große Bühne — nur jemanden,
            der es wirklich ernst nimmt."
          </p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52] mt-8">Lukas Stutz</p>
          <Ornament className="mt-9" />
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-16 text-left">
          {SAEULEN.map(([titel, text], i) => (
            <Reveal key={titel} delay={i * 120} testid={`vorschau-saeule-${i}`}>
              <p className="font-serif text-xl">{titel}</p>
              <span className="block w-10 h-px bg-[#BA6D52] mt-3" />
              <p className="text-sm text-[#6B5C50] mt-4 font-light leading-relaxed">{text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3 · Foto-Band */}
      <FotoBand src="/event-kueche.jpg" hoehe="h-[48vh] sm:h-[56vh]" dunkel={0.58}>
        <Reveal>
          <Kicker ton="text-[#E2C4B2]">Mein Handwerk</Kicker>
          <p className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#FFFBF4] mt-5 leading-tight drop-shadow-[0_3px_16px_rgba(0,0,0,0.9)]">
            In fremden Küchen<br className="hidden sm:block" /> wie in meiner eigenen.
          </p>
        </Reveal>
      </FotoBand>

      {/* 4 · Drei Wege */}
      <section className="bg-[#FDF8F1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 sm:py-28">
          <Reveal className="max-w-2xl">
            <Kicker>Drei Wege</Kicker>
            <Titel className="mt-5">Wie wir zusammenarbeiten.</Titel>
            <p className="mt-6 text-[#6B5C50] font-light leading-relaxed">
              Wählen Sie, was zu Ihnen passt — ich melde mich persönlich, meist innerhalb von
              24 Stunden.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
            {WEGE.map((w, i) => (
              <Reveal key={w.nr} delay={i * 130}>
                <Link to={w.to} data-testid={w.testid}
                  className="group relative block h-full bg-[#FFFDF8] border border-[#E6D8C2] hover:border-[#BA6D52] hover:-translate-y-1 hover:shadow-[0_28px_54px_-36px_rgba(120,80,48,0.6)] transition-all duration-500 overflow-hidden">
                  <div className="h-44 overflow-hidden">
                    <img src={w.bild} alt={w.title} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1300ms] ease-out group-hover:scale-110" />
                  </div>
                  <div className="p-7 flex flex-col">
                    <div className="flex items-start justify-between">
                      <w.icon size={26} strokeWidth={1.2} className="text-[#BA6D52]" />
                      <span className="font-serif text-3xl font-light text-[#E0D3C0] group-hover:text-[#BA6D52] transition-colors duration-300">
                        {w.nr}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl font-light mt-6 leading-snug">{w.title}</h3>
                    <p className="mt-4 text-sm text-[#6B5C50] font-light leading-relaxed">{w.text}</p>
                    <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-[#A08C7B]">{w.meta}</p>
                    <span className="mt-5 inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.2em] text-[#BA6D52]">
                      {w.cta}
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                  </div>
                  <span className="absolute left-0 bottom-0 h-px w-0 bg-[#BA6D52] transition-all duration-700 group-hover:w-full" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5 · Teller-Galerie */}
      <section className="bg-[#F7EADA] border-y border-[#EBD9C2]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 sm:py-28">
          <Reveal className="text-center">
            <Kicker>Impressionen</Kicker>
            <Titel className="mt-5">Teller, die in Erinnerung bleiben.</Titel>
            <Ornament className="mt-8" />
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-14 items-start">
            {GALERIE.map((g, i) => (
              <Reveal key={g.src} delay={i * 110} className={i % 2 === 1 ? "lg:mt-10" : ""}>
                <FotoKarte {...g} testid={`vorschau-galerie-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6 · Richtpreis */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 sm:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <Reveal className="md:col-span-6">
            <Kicker>Richtpreis Gastronomie</Kicker>
            <div className="mt-7 flex items-baseline gap-x-5 flex-wrap">
              <span className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter">49&nbsp;€</span>
              <span className="text-base text-[#8B7A68] font-light">/ Std netto · Werktag</span>
            </div>
            <p className="mt-6 text-sm text-[#6B5C50] font-light">
              An Sonn- &amp; Feiertagen <span className="text-[#BA6D52]">59 €</span> netto / Std.
              Für Privatkunden rechne ich pro Person — als Festpreis, inklusive Einkauf.
            </p>
          </Reveal>
          <Reveal delay={150} className="md:col-span-6 space-y-5">
            {[["Mindestbuchung", "4 Stunden"], ["Spontanbuchung (< 24 h)", "+ 10 %"],
              ["Anfahrt", "nach Absprache"]].map(([k, v]) => (
              <div key={k} className="border-t border-[#E6D8C2] pt-5 flex justify-between items-baseline gap-4">
                <span className="text-sm text-[#2B2320]">{k}</span>
                <span className="font-serif text-xl">{v}</span>
              </div>
            ))}
            <Link to="/preise" data-testid="vorschau-link-preise"
              className="inline-flex items-center gap-3 text-[#BA6D52] hover:text-[#9C4F31] text-[11px] uppercase tracking-[0.2em] pt-3 transition-colors">
              Preise im Detail <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 7 · Bewertungen */}
      {reviews.length > 0 && (
        <section id="vorschau-bewertungen" className="bg-[#FFFDF8] border-y border-[#EFE3D2] scroll-mt-20">
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 sm:py-28">
            <Reveal className="text-center">
              <span className="flex justify-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={16} className="fill-[#D99A5B] text-[#D99A5B]" />
                ))}
              </span>
              <Titel className="mt-6">{schnitt} von 5 Sternen</Titel>
              <p className="text-sm text-[#8B7A68] mt-4 font-light">
                {reviews.length} Bewertungen auf Google
              </p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
              {reviews.slice(0, 3).map((r, i) => (
                <Reveal key={r.id || i} delay={i * 120}>
                  <div className="relative h-full bg-[#FDF8F1] border border-[#E6D8C2] p-7"
                    data-testid={`vorschau-review-${i}`}>
                    <span className="absolute top-3 left-3 w-4 h-4 border-t border-l border-[#BA6D52]/50" />
                    <span className="absolute bottom-3 right-3 w-4 h-4 border-b border-r border-[#BA6D52]/50" />
                    <Quote size={18} className="text-[#BA6D52]" />
                    <p className="text-sm text-[#5A4D43] mt-5 font-light leading-relaxed italic">
                      „{r.text}"
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#A08C7B] mt-6">
                      {r.author}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            {googleUrl && (
              <div className="text-center mt-12">
                <GhostBtn href={googleUrl} target="_blank" rel="noreferrer"
                  data-testid="vorschau-google-link">
                  Alle Bewertungen auf Google
                </GhostBtn>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 8 · Gutschein */}
      <section className="bg-[#FDF8F1]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 sm:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src="/gutschein-foto.jpg" alt="Gutschein von Lukas Stutz" loading="lazy"
              data-testid="vorschau-gutschein-foto"
              className="w-full shadow-[0_34px_80px_-44px_rgba(120,80,48,0.7)]" />
          </Reveal>
          <Reveal delay={150}>
            <Gift size={22} className="text-[#BA6D52]" />
            <Titel className="mt-5">Ein Abend als Geschenk.</Titel>
            <p className="text-sm text-[#6B5C50] mt-6 font-light leading-relaxed">
              Handschriftlich ausgefüllt, mit Wachssiegel versehen und im Umschlag per Post —
              ein Gutschein für ein Menü in der eigenen Küche bleibt länger in Erinnerung als
              jedes Päckchen.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <PrimaryBtn href="/gutscheine" data-testid="vorschau-gutschein-cta">
                Gutschein ansehen <ArrowRight size={15} />
              </PrimaryBtn>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9 · Kontakt */}
      <section className="relative overflow-hidden text-[#FAF3E8]">
        <div className="absolute inset-0 bg-cover bg-center animate-kenburns"
          style={{ backgroundImage: "url('/event-bg-gericht.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(14,13,10,0.78)" }} />
        <div className="relative max-w-3xl mx-auto px-6 py-24 sm:py-32 text-center">
          <Reveal>
            <Kicker ton="text-[#E2C4B2]">Lassen Sie uns sprechen</Kicker>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mt-6 leading-tight">
              Ihr Termin ist vielleicht<br className="hidden sm:block" /> noch frei.
            </h2>
            <p className="text-sm md:text-base text-[#B3A89C] mt-6 font-light leading-relaxed max-w-xl mx-auto">
              Schreiben Sie mir kurz Anlass, Datum und Gästezahl — ich sage ehrlich, ob es passt,
              und melde mich meist innerhalb von 24 Stunden.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-11">
              <PrimaryBtn href="/anfrage-betrieb" data-testid="vorschau-final-cta">
                Jetzt anfragen <ArrowRight size={15} />
              </PrimaryBtn>
              <a href="/vorschau/kontakt" data-testid="vorschau-final-kontakt"
                className="inline-flex items-center justify-center gap-2.5 border border-white/40 text-[#FAF3E8] hover:bg-white/10 px-8 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors">
                Kontakt &amp; Erreichbarkeit
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-14 text-xs text-[#A89D91]">
              <span className="inline-flex items-center gap-2"><MessageCircle size={13} className="text-[#D99A5B]" /> WhatsApp</span>
              <span className="inline-flex items-center gap-2"><Phone size={13} className="text-[#D99A5B]" /> 0151 27282560</span>
              <span className="inline-flex items-center gap-2"><Mail size={13} className="text-[#D99A5B]" /> mietkoch@stutz-cuisine.com</span>
              <span className="inline-flex items-center gap-2"><CalendarCheck size={13} className="text-[#D99A5B]" /> Antwort meist in 24 h</span>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
